import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/notify";
import { logOperationalError } from "@/lib/operationalLogger";
import type { ClinicHoursSettings } from "@/lib/clinicHours";
import { DEFAULT_CLINIC_HOURS, sanitizeClinicHours } from "@/lib/clinicHours";
import type { Database } from "@/integrations/supabase/types";

const QUERY_KEY = ["clinic-settings"];
export const CLINIC_SETTINGS_SINGLETON_ID = "00000000-0000-0000-0000-000000000001";

type ClinicSettingsRow = Database["public"]["Tables"]["clinic_settings"]["Row"];
type ClinicSettingsQueryResult = {
  settings: ClinicHoursSettings;
  updatedAt: string | null;
};

export type SaveClinicSettingsResult = {
  ok: boolean;
  nextUpdatedAt?: string | null;
  issue?: "conflict" | "error";
};

async function fetchSettings(): Promise<ClinicSettingsQueryResult> {
  const { data, error } = await supabase
    .from("clinic_settings")
    .select("id, clinic_name, cnpj, timezone, schedule, address, website, phone, contact_email, wa_reminder_template, invite_email_template, low_package_threshold, low_mensalidade_threshold, desconto_promocional_mensalidade, monthly_discount_percent, monthly_closing_time, monthly_closing_timezone, created_at, updated_at")
    .eq("id", CLINIC_SETTINGS_SINGLETON_ID)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    return {
      settings: DEFAULT_CLINIC_HOURS,
      updatedAt: null,
    };
  }

  const row: ClinicSettingsRow = data;

  return {
    settings: sanitizeClinicHours({
      clinicName: row.clinic_name ?? DEFAULT_CLINIC_HOURS.clinicName,
      timezone: row.timezone ?? DEFAULT_CLINIC_HOURS.timezone,
      address: row.address ?? "",
      website: row.website ?? "",
      phone: row.phone ?? "",
      contactEmail: row.contact_email ?? "",
      waReminderTemplate: row.wa_reminder_template ?? "",
      inviteEmailTemplate: row.invite_email_template ?? "",
      lowPackageThreshold: row.low_package_threshold ?? 2,
      lowMensalidadeThreshold: row.low_mensalidade_threshold ?? 2,
      descontoPromocionalMensalidade: row.desconto_promocional_mensalidade ?? 0,
      schedule: row.schedule,
    }),
    updatedAt: row.updated_at,
  };
}

const mapSettingsToColumns = (settings: Partial<ClinicHoursSettings>) => {
  const payload: Database["public"]["Tables"]["clinic_settings"]["Update"] = {};

  if ("clinicName" in settings) payload.clinic_name = settings.clinicName;
  if ("timezone" in settings) payload.timezone = settings.timezone;
  if ("address" in settings) payload.address = settings.address;
  if ("website" in settings) payload.website = settings.website;
  if ("phone" in settings) payload.phone = settings.phone;
  if ("contactEmail" in settings) payload.contact_email = settings.contactEmail;
  if ("waReminderTemplate" in settings) payload.wa_reminder_template = settings.waReminderTemplate;
  if ("inviteEmailTemplate" in settings) payload.invite_email_template = settings.inviteEmailTemplate;
  if ("lowPackageThreshold" in settings) payload.low_package_threshold = settings.lowPackageThreshold;
  if ("lowMensalidadeThreshold" in settings) payload.low_mensalidade_threshold = settings.lowMensalidadeThreshold;
  if ("descontoPromocionalMensalidade" in settings) payload.desconto_promocional_mensalidade = settings.descontoPromocionalMensalidade;
  if ("schedule" in settings) payload.schedule = settings.schedule as unknown as Database["public"]["Tables"]["clinic_settings"]["Update"]["schedule"];

  return payload;
};

const mergeSettingsIntoCache = (
  currentSettings: ClinicHoursSettings | undefined,
  incomingSettings: Partial<ClinicHoursSettings>,
): ClinicHoursSettings =>
  sanitizeClinicHours({
    ...(currentSettings ?? DEFAULT_CLINIC_HOURS),
    ...incomingSettings,
  });

export function useClinicSettings() {
  const queryClient = useQueryClient();

  const query = useQuery<ClinicSettingsQueryResult>({
    queryKey: QUERY_KEY,
    queryFn: fetchSettings,
    staleTime: 1000 * 60 * 10, // 10 min — clinic settings rarely change
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const saveSettingsDetailed = async (
    settings: Partial<ClinicHoursSettings>,
    _expectedSnapshot?: string | null
  ): Promise<SaveClinicSettingsResult> => {
    const payload = {
      ...mapSettingsToColumns(settings),
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from("clinic_settings")
      .upsert({
        id: CLINIC_SETTINGS_SINGLETON_ID,
        ...payload,
      }, { onConflict: "id" })
      .select("updated_at")
      .single();

    if (error) {
      logOperationalError("clinic_settings_upsert_failed", error, {
        mode: "upsert",
        fields: Object.keys(settings),
      });
      toast.error(`Erro ao salvar configurações: ${error.message}`);
      return { ok: false, issue: "error" };
    }
    const nextUpdatedAt = data.updated_at ?? payload.updated_at;

    queryClient.setQueryData<ClinicSettingsQueryResult>(QUERY_KEY, (current) => ({
      settings: mergeSettingsIntoCache(current?.settings, settings),
      updatedAt: nextUpdatedAt,
    }));
    invalidate();
    return { ok: true, nextUpdatedAt };
  };

  const saveSettings = async (
    settings: Partial<ClinicHoursSettings>,
    expectedSnapshot?: string | null
  ) => {
    const result = await saveSettingsDetailed(settings, expectedSnapshot);
    return result.ok;
  };

  return {
    ...query,
    settings: query.data?.settings ?? DEFAULT_CLINIC_HOURS,
    updatedAt: query.data?.updatedAt ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    saveSettings,
    saveSettingsDetailed,
    invalidate,
  };
}
